/**
 * PultScreen — the screen in the middle of the pult: the queue, the catalogue, or the two decks
 * (task 54).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Экран, раскладка, поворот». The keys over it
 * choose what it shows (`store/pult.ts`, `screen`):
 *
 * - **QUEUE** — the queue sheet without its frame (`QueueBody`);
 * - **LIBRARY** — every track of the catalogue, the list of «Треки» (`TrackListView`);
 * - **VIEW** — the two decks: a track, the line of where it is, its tempo (`store/deck.ts`). Until
 *   there are waves to draw (the third package of the design) this is what VIEW has.
 *
 * In QUEUE and LIBRARY a tap on a row chooses the track for LOAD — it does not play it — and the
 * row chosen is lit; LOAD lays it on a deck (task 55).
 *
 * ## The app's lists, at the app's size
 *
 * The screen of variant B is the mixer's SCREEN tab (`.omc/wiki/pult-ekran.md` «Экран посреди
 * пульта»): the lists are the app's own, at the app's own size — the copy's print of a device,
 * laid out at twice the screen and drawn at half, could not be read without the magnifier, which is
 * gone. A row is a finger's target, as in the app's lists. The scroller is the screen's own, handed
 * to the list as the shell hands its own (`app/scroll-port.ts`).
 */

import type { JSX } from 'react';
import { useCallback, useState } from 'react';

import { ScrollPortContext } from '../app/scroll-port';
import type { TrackItem, TrackQuery } from '../lib/api';
import { formatDuration, formatPitch } from '../lib/format';
import type { DeckSide } from '../lib/pult-layout';
import { useDeck } from '../store/deck';
import { chooseTrack, usePult } from '../store/pult';

import { EMPTY_TRACKS_TEXT, LOADING_TEXT, REFRESH_AFTER_MS } from '../screens/Tracks';

import { QueueBody } from './QueueSheet';
import { TrackListView } from './TrackListView';

/** The catalogue, the same list as «Все треки». */
const ALL_TRACKS: TrackQuery = Object.freeze({ kind: 'all' });

export const LIBRARY_LIST_LABEL = 'Каталог на экране пульта';
/** VIEW: a deck with nothing on it. */
export const EMPTY_DECK_TEXT = 'Пусто';
export const DECKS_LABEL = 'Две деки';

function DeckLine({
  letter,
  track,
  positionS,
  durationS,
  pitch,
}: {
  readonly letter: DeckSide;
  readonly track: TrackItem | undefined;
  readonly positionS: number;
  readonly durationS: number;
  readonly pitch: number;
}): JSX.Element {
  const share = durationS > 0 ? Math.min(1, Math.max(0, positionS / durationS)) : 0;
  return (
    <section className="pultDeckLine" aria-label={`Дека ${letter}`} data-deck={letter}>
      <span className="pultDeckLetter" aria-hidden="true">
        {letter}
      </span>
      {track === undefined ? (
        <p className="pultDeckEmpty">{EMPTY_DECK_TEXT}</p>
      ) : (
        <>
          <p className="pultDeckTitle">{track.title}</p>
          <p className="pultDeckByline">{track.performer}</p>
          <div className="pultDeckPlace" aria-hidden="true">
            <div className="pultDeckPlaceFill" style={{ width: `${String(share * 100)}%` }} />
          </div>
          <p className="pultDeckNumbers">
            <span>{formatDuration(positionS)}</span>
            <span>{formatPitch(pitch)}</span>
            <span>{formatDuration(durationS)}</span>
          </p>
        </>
      )}
    </section>
  );
}

/** One deck as the player last wrote it into the deck slice. */
function DeckOnScreen({ side }: { readonly side: DeckSide }): JSX.Element {
  const track = useDeck((s) => s.decks[side].track);
  const positionS = useDeck((s) => s.decks[side].positionS);
  const durationS = useDeck((s) => s.decks[side].durationS);
  const pitch = useDeck((s) => s.decks[side].pitch);
  return (
    <DeckLine
      letter={side}
      track={track}
      positionS={positionS}
      durationS={durationS}
      pitch={pitch}
    />
  );
}

/** VIEW: both decks. */
function DecksView(): JSX.Element {
  return (
    <div className="pultDecks" role="group" aria-label={DECKS_LABEL}>
      <DeckOnScreen side="A" />
      <DeckOnScreen side="B" />
    </div>
  );
}

export function PultScreen(): JSX.Element {
  const view = usePult((s) => s.screen);
  const chosen = usePult((s) => s.chosen);
  const [port, setPort] = useState<HTMLElement | null>(null);
  const pick = useCallback((track: TrackItem): void => {
    chooseTrack(track);
  }, []);

  return (
    <div ref={setPort} className="pultScreen" data-shows={view}>
      <ScrollPortContext.Provider value={port}>
        {view === 'queue' ? (
          <QueueBody onPick={pick} pickedId={chosen?.id} />
        ) : view === 'library' ? (
          <TrackListView
            query={ALL_TRACKS}
            label={LIBRARY_LIST_LABEL}
            emptyText={EMPTY_TRACKS_TEXT}
            loadingText={LOADING_TEXT}
            maxAgeMs={REFRESH_AFTER_MS}
            onOpenTrack={pick}
            onPick={pick}
            pickedId={chosen?.id}
          />
        ) : (
          <DecksView />
        )}
      </ScrollPortContext.Provider>
    </div>
  );
}
